import React from "react";
import { Plus, Trash2, Moon } from "lucide-react";

const DAYS_CA = ["Dl", "Dm", "Dc", "Dj", "Dv", "Ds", "Dg"];
const WEEKEND = [6, 0];

// ─── Grid Header (thead) ──────────────────────────────────────────────────────
export function GridHeader({ weeks }) {
  return (
    <thead>
      <tr className="bg-gray-50 border-b border-gray-200">
        <th className="sticky left-0 z-10 bg-gray-50 text-left px-3 py-2 font-semibold text-gray-700 border-r border-gray-200 min-w-[150px]">
          Treballador/a
        </th>
        {weeks.map((weekDays, wi) => (
          <React.Fragment key={`wh-${wi}`}>
            {weekDays.map(d => {
              const isWeekend = WEEKEND.includes(d.day());
              return (
                <th
                  key={d.format("YYYY-MM-DD")}
                  className={`text-center py-1.5 font-medium border-r border-gray-100 ${isWeekend ? "bg-gray-100 text-gray-400" : "text-gray-600"}`}
                  style={{ minWidth: 94 }}
                >
                  <div className="text-[10px]">{DAYS_CA[(d.day() + 6) % 7]}</div>
                  <div className="font-bold text-gray-800 text-sm">{d.format("D")}</div>
                </th>
              );
            })}
            <th className="text-center px-2 py-1.5 font-bold text-indigo-700 bg-indigo-50 border-r border-indigo-200 min-w-[60px]">
              <div className="text-[9px] uppercase tracking-wide">Set {wi + 1}</div>
            </th>
          </React.Fragment>
        ))}
        <th className="text-center px-3 py-2 font-bold text-[#7B2D3F] bg-[#7B2D3F]/5 min-w-[70px]">
          <div className="text-[9px] uppercase tracking-wide">Total</div>
          <div className="text-[9px]">Mes</div>
        </th>
      </tr>
    </thead>
  );
}

// ─── Single Day Cell ──────────────────────────────────────────────────────────
export function GridDayCell({
  date, segs, isDirty, isWeekend, cellMins, isNightShift, minutesToHHMM,
  onUpdateSegment, onAddSegment, onRemoveSegment, email,
}) {
  return (
    <td
      className={`border-r border-gray-100 border-b border-gray-100 p-1 align-top ${
        isWeekend ? "bg-gray-50" : isDirty ? "bg-amber-50" : ""
      }`}
      style={{ minWidth: 94 }}
    >
      <div className="flex flex-col gap-1">
        {segs.map((seg, idx) => {
          const night = isNightShift(seg.entry, seg.exit);
          return (
            <div key={`seg-${date}-${idx}`} className="flex flex-col gap-0.5">
              {segs.length > 1 && (
                <div className="flex items-center justify-between">
                  <span className="text-[9px] text-gray-400 font-semibold">Tram {idx + 1}</span>
                  <button
                    onClick={() => onRemoveSegment(email, date, idx)}
                    className="text-gray-300 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-green-600 font-bold w-3 flex-shrink-0">E</span>
                <input
                  type="time"
                  value={seg.entry}
                  onChange={e => onUpdateSegment(email, date, idx, "entry", e.target.value)}
                  className={`flex-1 h-6 text-[11px] text-center border rounded outline-none focus:ring-1 focus:ring-[#7B2D3F]/40
                    ${isDirty ? "border-amber-300 bg-amber-100 text-amber-900" : "border-gray-200 bg-white text-gray-700"}
                    ${isWeekend ? "bg-gray-100 border-gray-200 text-gray-400" : ""}
                  `}
                />
              </div>
              <div className="flex items-center gap-0.5">
                <span className="text-[9px] text-red-500 font-bold w-3 flex-shrink-0">S</span>
                <input
                  type="time"
                  value={seg.exit}
                  onChange={e => onUpdateSegment(email, date, idx, "exit", e.target.value)}
                  className={`flex-1 h-6 text-[11px] text-center border rounded outline-none focus:ring-1 focus:ring-[#7B2D3F]/40
                    ${isDirty ? "border-amber-300 bg-amber-100 text-amber-900" : "border-gray-200 bg-white text-gray-700"}
                    ${isWeekend ? "bg-gray-100 border-gray-200 text-gray-400" : ""}
                  `}
                />
                {night && <Moon className="w-2.5 h-2.5 text-indigo-400 flex-shrink-0" title="Torn nocturn: sortida dia següent" />}
              </div>
            </div>
          );
        })}
        <button
          onClick={() => onAddSegment(email, date)}
          className="flex items-center justify-center gap-0.5 text-[9px] text-gray-400 hover:text-[#7B2D3F] transition-colors mt-0.5"
          title="Afegir tram"
        >
          <Plus className="w-2.5 h-2.5" /> tram
        </button>
        {cellMins > 0 && (
          <div className="text-center text-[10px] font-bold text-[#7B2D3F] leading-tight border-t border-gray-100 pt-0.5">
            {minutesToHHMM(cellMins)}
          </div>
        )}
      </div>
    </td>
  );
}

// ─── Weekly Summary Cell ──────────────────────────────────────────────────────
export function WeeklySummaryCell({ actual, contracted, minutesToHHMM }) {
  const diff = actual - contracted;
  return (
    <td className="text-center px-1 py-2 bg-indigo-50/70 border-r border-indigo-200 border-b border-indigo-100 align-middle">
      <div>
        <div className="font-bold text-indigo-700 text-[11px]">
          {actual > 0 ? minutesToHHMM(actual) : "–"}
        </div>
        {contracted > 0 && actual > 0 && (
          <div className={`text-[9px] font-semibold ${diff > 0 ? "text-red-500" : diff < 0 ? "text-amber-500" : "text-emerald-500"}`}>
            {diff > 0 ? `+${minutesToHHMM(diff)}` : diff < 0 ? `-${minutesToHHMM(Math.abs(diff))}` : "="}
          </div>
        )}
      </div>
    </td>
  );
}

// ─── Monthly Summary Cell ─────────────────────────────────────────────────────
export function MonthlySummaryCell({ actual, contracted, minutesToHHMM }) {
  const diff = actual - contracted;
  return (
    <td className="text-center px-2 py-2 border-b border-gray-100 bg-[#7B2D3F]/5 align-middle">
      <div>
        <div className="font-bold text-[#7B2D3F] text-sm">
          {actual > 0 ? minutesToHHMM(actual) : "–"}
        </div>
        {contracted > 0 && (
          <div className="text-[9px] text-gray-400">
            /{minutesToHHMM(contracted)}
          </div>
        )}
        {contracted > 0 && actual > 0 && (
          <div className={`text-[9px] font-semibold ${diff > 0 ? "text-red-500" : diff < 0 ? "text-amber-500" : "text-emerald-500"}`}>
            {diff > 0 ? `+${minutesToHHMM(diff)}` : diff < 0 ? `-${minutesToHHMM(Math.abs(diff))}` : "="}
          </div>
        )}
      </div>
    </td>
  );
}
